import type {
  AccountLocks,
  AdsLockPlatformKey,
  SocialIdentityLock,
  SocialLockPlatformKey,
} from "../types/accountLocks";
import { getLockedAccountMessage } from "../types/accountLocks";

export {
  ACCOUNT_LOCK_MESSAGE,
  getLockedAccountMessage,
} from "../types/accountLocks";
export type {
  AccountLocks,
  AdsAccountLock,
  AdsLockPlatformKey,
  SocialIdentityLock,
  SocialLockPlatformKey,
} from "../types/accountLocks";

/** Map AccountConnection type string → ads lock key */
export const ADS_TYPE_TO_LOCK_KEY: Record<string, AdsLockPlatformKey> = {
  FACEBOOK: "facebookAds",
  INSTAGRAM: "facebookAds",
  GOOGLE: "googleAdwords",
  LINKEDIN: "linkedinAds",
  TIKTOK: "tiktokAds",
  SNAPCHAT: "snapchatAds",
  BING_ADS: "bingAds",
  SPOTIFY: "spotifyAds",
  OPENAI_ADS: "openAiAds",
};

export const getAccountLocks = (workspaceUserData?: any): AccountLocks => {
  return (workspaceUserData?.accountLocks || {}) as AccountLocks;
};

export const normalizeAdsAccountId = (
  accountId?: string | number | null
): string => {
  if (accountId == null) return "";
  return String(accountId).replace(/\D/g, "") || String(accountId);
};

export const isAdsAccountAllowed = (
  locks: AccountLocks | undefined,
  platformKey: AdsLockPlatformKey,
  accountId?: string | null
): boolean => {
  const locked = locks?.ads?.[platformKey];
  if (!locked?.accountId) return true;
  if (!accountId) return false;
  return (
    normalizeAdsAccountId(locked.accountId) ===
    normalizeAdsAccountId(accountId)
  );
};

export const isSocialIdentityAllowed = (
  locks: AccountLocks | undefined,
  socialKey: SocialLockPlatformKey,
  identity: Partial<SocialIdentityLock>
): boolean => {
  const locked = locks?.social?.[socialKey];
  if (!locked) return true;

  if (socialKey === "facebook" && locked.pageId) {
    return (
      Boolean(identity.pageId) &&
      String(identity.pageId) === String(locked.pageId)
    );
  }
  if (socialKey === "instagram" && locked.instaAccId) {
    return (
      Boolean(identity.instaAccId) &&
      String(identity.instaAccId) === String(locked.instaAccId)
    );
  }
  if (socialKey === "tiktok" && locked.openId) {
    return (
      Boolean(identity.openId) &&
      String(identity.openId) === String(locked.openId)
    );
  }
  if (socialKey === "youtube" && locked.channelId) {
    return (
      Boolean(identity.channelId) &&
      String(identity.channelId) === String(locked.channelId)
    );
  }
  if (socialKey === "linkedin") {
    if (locked.postingPreference && identity.postingPreference) {
      if (locked.postingPreference !== identity.postingPreference) return false;
    }
    if (locked.postingPreference === "COMPANY" && locked.postingOrganizationId) {
      return (
        String(identity.postingOrganizationId || "") ===
        String(locked.postingOrganizationId)
      );
    }
    return (
      !locked.postingPreference ||
      locked.postingPreference === identity.postingPreference
    );
  }

  return true;
};

export const getAdsLockRejectionMessage = (
  locks: AccountLocks | undefined,
  platformKey: AdsLockPlatformKey
): string => {
  const locked = locks?.ads?.[platformKey];
  return getLockedAccountMessage(locked?.accountName || locked?.accountId);
};

export const getSocialLockRejectionMessage = (
  locks: AccountLocks | undefined,
  socialKey: SocialLockPlatformKey
): string => {
  const locked = locks?.social?.[socialKey];
  const name =
    locked?.pageName ||
    locked?.instaUsername ||
    locked?.displayName ||
    locked?.pageId ||
    locked?.instaAccId ||
    locked?.channelId ||
    locked?.openId;
  return getLockedAccountMessage(name);
};

/** Extract selectable account id from a platform list item */
export const getAccountIdFromSelectionItem = (
  item: any,
  type: string
): string | undefined => {
  if (!item) return undefined;
  if (type === "GOOGLE" || type === "GOOGLE_ANALYTICS") {
    const raw =
      item.customerId != null
        ? item.customerId
        : item.id != null
          ? item.id
          : undefined;
    return raw != null ? String(raw) : undefined;
  }
  if (type === "TIKTOK") {
    return item.advertiser_id != null
      ? String(item.advertiser_id)
      : item.id != null
        ? String(item.id)
        : undefined;
  }
  if (type === "TIKTOK_SOCIAL") {
    return item.open_id != null
      ? String(item.open_id)
      : item.openId != null
        ? String(item.openId)
        : undefined;
  }
  if (type === "GOOGLE_SOCIAL") {
    return item.id != null
      ? String(item.id)
      : item.channelId != null
        ? String(item.channelId)
        : undefined;
  }
  if (item.id != null) return String(item.id);
  if (item.account_id != null) return String(item.account_id);
  return undefined;
};
